import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { CalendarCheck, Check, Plus, RefreshCw, Wallet, BookOpen, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loading, PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
function usePersonal(path) {
  const [data, setData] = useState(null),
    [error, setError] = useState("");
  const load = () => {
    setError("");
    api.get(path).then(r => setData(r.data)).catch(e => setError(errMsg(e)));
  };
  useEffect(() => {
    let live = true;
    setData(null);
    setError("");
    api.get(path).then(r => {
      if (live) setData(r.data);
    }).catch(e => {
      if (live) setError(errMsg(e));
    });
    return () => {
      live = false;
    };
  }, [path]);
  return {
    data,
    error,
    load,
    setData
  };
}
function State({
  error,
  data,
  load,
  children
}) {
  return error ? <div role="alert" className="panel space-y-3 p-6"><p>{error}</p><Button variant="outline" onClick={load}>Try again</Button></div> : !data ? <Loading /> : children;
}
function Field({
  label,
  children
}) {
  return <label className="block space-y-2"><span className="text-xs font-medium">{label}</span>{children}</label>;
}
function Select({
  value,
  onChange,
  options,
  label
}) {
  return <select aria-label={label} value={value} onChange={e => onChange(e.target.value)} className="h-10 w-full rounded-md border bg-background px-3 text-sm">{options.map(v => <option key={v} value={v}>{v.replaceAll("_", " ")}</option>)}</select>;
}
function Metrics({
  items
}) {
  return <div className="grid gap-3 sm:grid-cols-3">{items.map(([label, value, hint]) => <div className="panel p-5" key={label}><p className="eyebrow">{label}</p><p className="mt-2 text-3xl font-bold">{value}</p><p className="mt-2 text-xs text-muted-foreground">{hint}</p></div>)}</div>;
}
function Blank({
  icon: Icon,
  title,
  text
}) {
  return <div className="panel flex flex-col items-center p-10 text-center"><span className="mb-4 rounded-2xl bg-primary/5 p-4 text-primary"><Icon size={28} /></span><h2 className="section-title">{title}</h2><p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">{text}</p></div>;
}
function Tabs({
  values,
  value,
  onChange
}) {
  return <div className="flex flex-wrap gap-2" aria-label="Filter records">{values.map(v => <Button key={v} size="sm" variant={v === value ? "default" : "outline"} onClick={() => onChange(v)} aria-pressed={v === value} className="capitalize">{v.replaceAll("_", " ")}</Button>)}</div>;
}
const localDate = iso => iso ? new Date(new Date(iso).getTime() - new Date(iso).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "";
const stamp = iso => iso ? new Date(iso).toLocaleString(undefined, {
  dateStyle: "medium",
  timeStyle: "short"
}) : "No date set";
const taskInitial = {
  title: "",
  notes: "",
  due_at: "",
  priority: "normal",
  application_id: null
};
const taskBody = (f, status) => ({
  title: f.title,
  notes: f.notes,
  due_at: f.due_at ? new Date(f.due_at).toISOString() : null,
  priority: f.priority,
  application_id: f.application_id || null,
  ...(f.id ? {
    version: f.version,
    status: status || f.status
  } : {})
});
export function Tasks() {
  const state = usePersonal("/personal/tasks"),
    {
      data,
      load
    } = state;
  const [filter, setFilter] = useState("active"),
    [query, setQuery] = useState(""),
    [form, setForm] = useState(null),
    [busy, setBusy] = useState(false);
  const change = async (task, status) => {
    setBusy(true);
    try {
      await api.put(`/personal/tasks/${task.id}`, taskBody(task, status));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const save = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      await api[form.id ? "put" : "post"](`/personal/tasks${form.id ? `/${form.id}` : ""}`, taskBody(form));
      setForm(null);
      load();
      toast.success("Task saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const items = data?.items || [],
    active = items.filter(t => ["todo", "in_progress"].includes(t.status));
  const shown = items.filter(t => (filter === "all" || (filter === "active" ? ["todo", "in_progress"].includes(t.status) : t.status === filter)) && `${t.title} ${t.notes}`.toLowerCase().includes(query.toLowerCase()));
  return <div className="space-y-6"><PageHeader eyebrow="One clear next step" title="Today & tasks"><Button onClick={() => setForm({
        ...taskInitial
      })}><Plus size={16} />New task</Button></PageHeader>
    <section className="workspace-banner"><p className="text-xs text-blue-200">YOUR DAILY FOCUS</p><h2 className="mt-3 text-2xl font-bold">Turn your search into small, deliberate moves.</h2><p className="mt-3 max-w-xl text-sm text-blue-100/80">Prepare a story, follow up on an application, or review a document. Keep your next action and its deadline together.</p><div className="mt-5 flex flex-wrap gap-3"><Button asChild className="bg-white text-blue-950 hover:bg-blue-50"><Link to="/interviews">Interview planner<ArrowUpRight size={14} /></Link></Button><Button asChild variant="outline" className="border-white/20 bg-white/10 text-white hover:bg-white/20"><Link to="/practice">Prepare an answer</Link></Button></div></section>
    <Metrics items={[["Open tasks", active.length, "To do and in progress"], ["Overdue", active.filter(t => t.due_at && new Date(t.due_at) < new Date()).length, "Deadlines in your device time zone"], ["Completed", items.filter(t => t.status === "done").length, "Your completed actions"]]} />
    <div className="flex flex-wrap justify-between gap-3"><Tabs values={["active", "done", "archived", "all"]} value={filter} onChange={setFilter} /><Input aria-label="Search tasks" placeholder="Search tasks…" className="sm:max-w-64" value={query} onChange={e => setQuery(e.target.value)} /></div>
    <State {...state}>{shown.length ? <div className="space-y-3">{shown.map(t => <article key={t.id} className="panel flex flex-wrap items-start gap-4 p-5"><span className="rounded-xl bg-primary/5 p-3 text-primary"><CalendarCheck size={21} /></span><div className="min-w-0 flex-1 basis-48"><p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t.priority} priority · {t.status.replaceAll("_", " ")}</p><h2 className="mt-1 font-semibold break-words">{t.title}</h2><p className="mt-2 whitespace-pre-wrap break-words text-xs text-muted-foreground">{t.notes}</p><p className={`mt-3 text-xs ${t.due_at && new Date(t.due_at) < new Date() && t.status !== "done" ? "text-amber-600" : "text-muted-foreground"}`}>{stamp(t.due_at)}</p>{t.application_id && <Link to="/tracker" className="mt-2 block text-xs text-primary">Linked application →</Link>}</div><div className="flex flex-wrap gap-2"><Button size="sm" variant="outline" disabled={busy} onClick={() => setForm({
              ...t,
              due_at: localDate(t.due_at)
            })}>Edit</Button>{t.status === "todo" && <Button size="sm" variant="outline" disabled={busy} onClick={() => change(t, "in_progress")}>Start</Button>}{["todo", "in_progress"].includes(t.status) && <Button size="sm" disabled={busy} onClick={() => change(t, "done")}><Check size={14} />Done</Button>}<Button size="sm" variant="ghost" disabled={busy} onClick={() => change(t, t.status === "archived" || t.status === "done" ? "todo" : "archived")}>{t.status === "archived" || t.status === "done" ? "Reopen" : "Archive"}</Button></div></article>)}</div> : <Blank icon={CalendarCheck} title="A little space to focus" text="Add a concrete next action. Finished and archived tasks stay available in their own views." />}
    {!!data?.application_reminders.length && <section className="panel p-5"><h2 className="section-title">Application follow-ups</h2><p className="mt-1 text-xs text-muted-foreground">Reminders from your application tracker.</p>{data.application_reminders.map(r => <div className="flex flex-wrap justify-between gap-3 border-b py-4 last:border-0" key={r.id}><div><p className="text-sm">{r.text}</p><p className="mt-1 text-xs text-muted-foreground">{r.company} · {stamp(r.at)}</p></div><Button asChild size="sm" variant="outline"><Link to="/tracker">Open tracker</Link></Button></div>)}</section>}</State>
    <Dialog open={!!form} onOpenChange={v => {
      if (!v && !busy) setForm(null);
    }}><DialogContent><DialogHeader><DialogTitle>{form?.id ? "Edit task" : "Plan your next action"}</DialogTitle><DialogDescription>Choose a realistic deadline. Times use your device's time zone.</DialogDescription></DialogHeader>{form && <form className="space-y-4" onSubmit={save}><Field label="Task"><Input required maxLength={200} value={form.title} onChange={e => setForm({
              ...form,
              title: e.target.value
            })} /></Field><Field label="Notes"><Textarea maxLength={4000} value={form.notes} onChange={e => setForm({
              ...form,
              notes: e.target.value
            })} /></Field><div className="grid gap-4 sm:grid-cols-2"><Field label="Due date (optional)"><Input type="datetime-local" value={form.due_at} onChange={e => setForm({
                ...form,
                due_at: e.target.value
              })} /></Field><Field label="Priority"><Select label="Priority" options={["low", "normal", "high"]} value={form.priority} onChange={priority => setForm({
                ...form,
                priority
              })} /></Field></div><Button type="submit" disabled={busy}>{busy ? "Saving…" : "Save task"}</Button></form>}</DialogContent></Dialog>
  </div>;
}
const offerInitial = {
  company: "",
  title: "",
  currency: "INR",
  base: "",
  period: "year",
  variable: "0",
  signing_bonus: "0",
  equity_note: "",
  location: "",
  deadline: "",
  notes: "",
  application_id: null
};
const offerBody = f => ({
  ...Object.fromEntries(Object.keys(offerInitial).map(k => [k, f[k]])),
  deadline: f.deadline ? new Date(f.deadline).toISOString() : null,
  ...(f.id ? {
    version: f.version,
    status: f.status
  } : {})
});
const money = (minor, currency) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2
    }).format(minor / 100);
  } catch {
    return `${currency} ${(minor / 100).toFixed(2)}`;
  }
};
export function Offers() {
  const state = usePersonal("/personal/offers"),
    {
      data,
      load
    } = state;
  const [form, setForm] = useState(null),
    [busy, setBusy] = useState(false),
    [filter, setFilter] = useState("active"),
    [currency, setCurrency] = useState("INR");
  const all = data || [],
    shown = all.filter(o => filter === "all" || (filter === "active" ? ["reviewing", "negotiating", "accepted"].includes(o.status) : o.status === filter));
  const compared = shown.filter(o => o.currency === currency && !["declined", "archived"].includes(o.status)).sort((a, b) => b.annual_base_minor - a.annual_base_minor);
  const save = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      await api[form.id ? "put" : "post"](`/personal/offers${form.id ? `/${form.id}` : ""}`, offerBody(form));
      setForm(null);
      load();
      toast.success("Offer record saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return <div className="space-y-6"><PageHeader eyebrow="Make the next move with clarity" title="Offers & decisions"><Button onClick={() => setForm({
        ...offerInitial
      })}><Plus size={16} />Add offer</Button></PageHeader><section className="workspace-banner"><p className="text-xs text-blue-200">MORE THAN A HEADLINE NUMBER</p><h2 className="mt-3 text-2xl font-bold">Compare the guaranteed. Understand the possible.</h2><p className="mt-3 max-w-2xl text-sm text-blue-100/80">Record the details from your offer letter. Annual base, potential variable pay and one-time bonuses stay separate, so you can make a considered decision.</p></section>
    <Metrics items={[["Under review", all.filter(o => o.status === "reviewing").length, "Your recorded offers"], ["Negotiating", all.filter(o => o.status === "negotiating").length, "Conversations in progress"], ["Decision dates", all.filter(o => o.deadline && ["reviewing", "negotiating"].includes(o.status)).length, "Keep an eye on your deadlines"]]} /><Tabs values={["active", "declined", "archived", "all"]} value={filter} onChange={setFilter} /><State {...state}>
    {all.length > 0 && <section className="panel p-5"><div className="flex flex-wrap items-center justify-between gap-3"><h2 className="section-title">Annual base comparison</h2><div className="w-28"><Select label="Comparison currency" value={currency} onChange={setCurrency} options={[...new Set(["INR", ...all.map(o => o.currency)])]} /></div></div><p className="mt-2 text-xs text-muted-foreground">Same currency only · before tax · no exchange-rate estimates</p><div className="mt-5 space-y-4">{!compared.length && <p className="text-xs text-muted-foreground">No active offers in this currency and view.</p>}{compared.map(o => <div key={o.id}><div className="mb-2 flex flex-wrap justify-between gap-2 text-xs"><span>{o.company}</span><strong>{money(o.annual_base_minor, o.currency)}</strong></div><div className="h-2 rounded bg-muted"><div className="h-full rounded bg-primary/70" style={{
                width: `${compared[0].annual_base_minor ? o.annual_base_minor / compared[0].annual_base_minor * 100 : 0}%`
              }} /></div></div>)}</div></section>}
    {shown.length ? <div className="grid gap-5 xl:grid-cols-2">{shown.map(o => <article className="panel p-6" key={o.id}><div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className="eyebrow break-words">{o.company}</p><h2 className="mt-2 section-title break-words">{o.title}</h2><p className="mt-1 text-xs text-muted-foreground">{o.location || "Location not recorded"} · {o.status}</p></div><Button size="sm" variant="outline" onClick={() => setForm({
              ...o,
              deadline: localDate(o.deadline)
            })}>Edit</Button></div><p className="mt-6 text-xs text-muted-foreground">Guaranteed annual base</p><p className="mt-1 break-words text-2xl font-bold">{money(o.annual_base_minor, o.currency)}</p><div className="mt-5 grid grid-cols-2 gap-4 border-y py-4 text-xs"><div><p className="text-muted-foreground">Potential annual variable</p><p className="mt-2 font-semibold">{money(o.annual_variable_minor, o.currency)}</p></div><div><p className="text-muted-foreground">One-time signing bonus</p><p className="mt-2 font-semibold">{money(o.signing_bonus_minor, o.currency)}</p></div></div>{o.equity_note && <p className="mt-4 text-xs break-words">Equity: {o.equity_note}</p>}{o.notes && <p className="mt-3 whitespace-pre-wrap break-words text-xs text-muted-foreground">{o.notes}</p>}<p className="mt-4 text-xs text-muted-foreground">Decision: {stamp(o.deadline)}</p></article>)}</div> : <Blank icon={Wallet} title="Make room for a good decision" text="Add an offer to compare pay, location, deadlines and your own notes. All details are entered by you." />}</State><p className="text-xs leading-relaxed text-muted-foreground">These are your personal records. Updating a status does not contact an employer or accept an offer.</p>
    <Dialog open={!!form} onOpenChange={v => {
      if (!v && !busy) setForm(null);
    }}><DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>{form?.id ? "Review offer details" : "Record an offer"}</DialogTitle><DialogDescription>Enter amounts from the offer letter. Variable pay is annual; signing bonus is one-time.</DialogDescription></DialogHeader>{form && <form onSubmit={save} className="space-y-4">{[["company", "Company"], ["title", "Role"], ["location", "Location"]].map(([k, l]) => <Field key={k} label={l}><Input required={k !== "location"} maxLength={k === "company" ? 150 : 200} value={form[k]} onChange={e => setForm({
              ...form,
              [k]: e.target.value
            })} /></Field>)}<div className="grid grid-cols-2 gap-3"><Field label="Currency code"><Input pattern="[A-Z]{3}" required maxLength={3} value={form.currency} onChange={e => setForm({
                ...form,
                currency: e.target.value.toUpperCase()
              })} /></Field><Field label="Base pay period"><Select label="Pay period" value={form.period} options={["year", "month"]} onChange={period => setForm({
                ...form,
                period
              })} /></Field></div>{[["base", "Base pay"], ["variable", "Potential annual variable pay"], ["signing_bonus", "One-time signing bonus"]].map(([k, l]) => <Field key={k} label={l}><Input type="number" min="0" max="9999999999.99" step="0.01" required value={form[k]} onChange={e => setForm({
              ...form,
              [k]: e.target.value
            })} /></Field>)}<Field label="Equity details (not valued)"><Textarea maxLength={1000} value={form.equity_note} onChange={e => setForm({
              ...form,
              equity_note: e.target.value
            })} /></Field><Field label="Decision deadline"><Input type="datetime-local" value={form.deadline} onChange={e => setForm({
              ...form,
              deadline: e.target.value
            })} /></Field><Field label="Your notes"><Textarea maxLength={4000} value={form.notes} onChange={e => setForm({
              ...form,
              notes: e.target.value
            })} /></Field>{form.id && <Field label="Record status"><Select label="Offer status" value={form.status} options={["reviewing", "negotiating", "accepted", "declined", "archived"]} onChange={status => setForm({
              ...form,
              status
            })} /></Field>}<Button type="submit" disabled={busy}>{busy ? "Saving…" : "Save offer"}</Button></form>}</DialogContent></Dialog>
  </div>;
}
export function Practice() {
  const state = usePersonal("/personal/practice"),
    nav = useNavigate();
  const [role, setRole] = useState(""),
    [busy, setBusy] = useState(false),
    [filter, setFilter] = useState("active");
  const start = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/personal/practice", {
        role
      });
      nav(`/practice/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const sessions = (state.data || []).filter(s => filter === "all" || (filter === "active" ? s.status !== "archived" : s.status === filter));
  return <div className="space-y-6"><PageHeader eyebrow="Prepare with evidence" title="Interview practice" /><section className="workspace-banner"><p className="text-xs text-blue-200">GUIDED PRACTICE WORKBOOK</p><h2 className="mt-3 text-2xl font-bold">Your experience. A clearer story.</h2><p className="mt-3 max-w-xl text-sm text-blue-100/80">Work through five role-based prompts. Connect your answer to confirmed career facts and review your own clarity, contribution and evidence.</p></section><form className="panel space-y-4 p-5" onSubmit={start}><Label htmlFor="practice-role">Which role are you preparing for?</Label><div className="flex flex-col gap-3 sm:flex-row"><Input id="practice-role" required minLength={2} maxLength={200} value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. Business Development Manager" /><Button disabled={busy} type="submit"><Plus size={16} />{busy ? "Creating…" : "Start workbook"}</Button></div><p className="text-xs text-muted-foreground">Structured questions and self-review. No automated AI scoring.</p></form><Tabs values={["active", "completed", "archived", "all"]} value={filter} onChange={setFilter} /><State {...state}>{sessions.length ? <div className="grid gap-4 md:grid-cols-2">{sessions.map(s => <Link key={s.id} to={`/practice/${s.id}`} className="panel p-5 hover:border-primary/40"><div className="flex items-center justify-between"><BookOpen size={22} className="text-primary" /><span className="text-xs capitalize text-muted-foreground">{s.status}</span></div><h2 className="mt-4 section-title break-words">{s.role}</h2><p className="mt-2 text-xs text-muted-foreground">{Object.values(s.answers).filter(a => a.text?.trim()).length} of {s.questions.length} answers written</p><p className="mt-4 text-xs text-primary">Continue workbook →</p></Link>)}</div> : <Blank icon={BookOpen} title="A strong answer starts with a real example" text="Start a workbook for your target role. Your drafts are private and saved when you choose Save." />}</State></div>;
}
export function PracticeDetail() {
  const {
      id
    } = useParams(),
    state = usePersonal(`/personal/practice/${id}`),
    {
      data,
      setData
    } = state;
  const [answers, setAnswers] = useState({}),
    [busy, setBusy] = useState(false),
    [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (data) {
      setAnswers(data.answers);
      setDirty(false);
    }
  }, [data]);
  useEffect(() => {
    const warn = e => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    const leave = e => {
      const anchor = e.target.closest?.("a[href]");
      if (dirty && anchor && !anchor.target && anchor.href !== window.location.href && !window.confirm("You have unsaved answers. Leave without saving?")) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", warn);
    document.addEventListener("click", leave, true);
    return () => {
      window.removeEventListener("beforeunload", warn);
      document.removeEventListener("click", leave, true);
    };
  }, [dirty]);
  const update = (qid, field, value) => {
    setAnswers(a => ({
      ...a,
      [qid]: {
        text: "",
        fact_ids: [],
        clear_context: false,
        specific_action: false,
        supported_result: false,
        ...a[qid],
        [field]: value
      }
    }));
    setDirty(true);
  };
  const save = async status => {
    setBusy(true);
    try {
      const r = await api.put(`/personal/practice/${id}`, {
        version: data.version,
        answers,
        status
      });
      setData({
        ...r.data,
        confirmed_facts: data.confirmed_facts
      });
      toast.success("Workbook saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return <div className="space-y-6"><PageHeader eyebrow="Guided workbook · self-review" title={data?.role || "Interview workbook"}><Button asChild variant="outline"><Link to="/practice" >All workbooks</Link></Button></PageHeader><State {...state}>{data && <><div className="panel flex flex-wrap items-center justify-between gap-3 p-5"><div><p className="text-sm font-medium capitalize">{data.status} · version {data.version}</p><p className="mt-1 text-xs text-muted-foreground">{dirty ? "Unsaved changes — save before navigating away." : "All changes saved."} Answers are your own; check every claim.</p></div><div className="flex flex-wrap gap-2"><Button variant="outline" disabled={busy} onClick={() => save("draft")}>Save draft</Button><Button disabled={busy} onClick={() => save("completed")}>Complete workbook</Button><Button variant="ghost" disabled={busy} onClick={() => save(data.status === "archived" ? "draft" : "archived")}>{data.status === "archived" ? "Restore" : "Archive"}</Button></div></div>{data.questions.map((q, i) => {
          const a = answers[q.id] || {};
          return <section className="panel p-5 sm:p-6" key={q.id}><p className="eyebrow">Question {i + 1} of {data.questions.length}</p><h2 className="mt-3 text-lg font-semibold">{q.text}</h2><Field label="Your answer"><Textarea disabled={busy} className="mt-4 min-h-40" maxLength={6000} value={a.text || ""} onChange={e => update(q.id, "text", e.target.value)} placeholder="Set the scene, explain your action, then give a result you can support." /></Field><details className="mt-4 rounded-lg border p-3"><summary className="cursor-pointer text-xs font-medium">Supporting career facts ({a.fact_ids?.length || 0} selected)</summary><div className="mt-3 max-h-56 space-y-3 overflow-y-auto">{data.confirmed_facts.map(f => <label key={f.id} className="flex gap-3 text-xs"><input disabled={busy} type="checkbox" checked={a.fact_ids?.includes(f.id) || false} onChange={e => update(q.id, "fact_ids", e.target.checked ? [...(a.fact_ids || []), f.id] : a.fact_ids.filter(v => v !== f.id))} /><span>{f.text}</span></label>)}{!data.confirmed_facts.length && <Link to="/profile" className="text-xs text-primary">Confirm career facts in your profile first →</Link>}</div></details><div className="mt-5 flex flex-wrap gap-4">{[["clear_context", "Clear context"], ["specific_action", "My specific contribution"], ["supported_result", "Result I can support"]].map(([k, l]) => <label key={k} className="flex items-center gap-2 text-xs"><input disabled={busy} type="checkbox" checked={!!a[k]} onChange={e => update(q.id, k, e.target.checked)} />{l}</label>)}</div></section>;
        })}<p className="text-xs text-muted-foreground">Self-review checkboxes are your assessment. They do not verify the answer or represent an employer's evaluation.</p></>}</State></div>;
}
export function Insights() {
  const state = usePersonal("/personal/insights"),
    d = state.data;
  return <div className="space-y-6"><PageHeader eyebrow="Understand your own momentum" title="Personal insights"><Button variant="outline" onClick={state.load}><RefreshCw size={14} />Refresh</Button></PageHeader><State {...state}>{d && <><Metrics items={[["Tracked opportunities", d.applications, "Current personal pipeline"], ["Confirmed facts", d.facts.confirmed || 0, "Your document source of truth"], ["Awaiting review", d.facts.unconfirmed || 0, "Facts to check in your profile"]]} /><section className="panel p-5 sm:p-6"><h2 className="section-title">Eight weeks of activity</h2><p className="mt-2 text-xs text-muted-foreground">Weekly saved jobs and applications marked applied by you. UTC weeks; each application counts once per week.</p><div className="mt-6 space-y-4">{d.weeks.map(w => <div key={w.week} className="grid grid-cols-[60px_1fr] items-center gap-4"><span className="text-xs text-muted-foreground">{new Date(w.week).toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "short",
                  timeZone: "UTC"
                })}</span><div className="space-y-1.5">{[["Saved", w.saved, "bg-primary/70"], ["Applied", w.applied, "bg-teal-600/70"]].map(([label, n, color]) => <div key={label} className="flex items-center gap-2"><div className="h-2 flex-1 rounded bg-muted"><div className={`h-full rounded ${color}`} style={{
                      width: `${n / Math.max(1, ...d.weeks.flatMap(x => [x.saved, x.applied])) * 100}%`
                    }} /></div><span className="w-20 text-right text-[11px]">{label} {n}</span></div>)}</div></div>)}</div></section><div className="grid items-start gap-6 xl:grid-cols-2"><section className="panel p-5"><h2 className="section-title">Employer overview</h2>{d.employers.length ? d.employers.map(e => <div key={e.company} className="border-b py-4 last:border-0"><p className="text-sm font-medium break-words">{e.company} <span className="text-muted-foreground">· {e.tracked}</span></p><div className="mt-2 flex flex-wrap gap-2">{Object.entries(e.statuses).map(([s, n]) => <span className="rounded bg-muted px-2 py-1 text-[11px]" key={s}>{s}: {n}</span>)}</div></div>) : <p className="mt-4 text-sm text-muted-foreground">Save your first opportunity to see employer activity.</p>}</section><section className="panel p-5"><h2 className="section-title">Your recent activity</h2><p className="mt-1 text-xs text-muted-foreground">Latest 30 recorded actions in your account</p>{d.activity.map(a => <div className="flex gap-3 border-b py-3 last:border-0" key={a.id}><span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary/50" /><div className="min-w-0"><p className="break-words text-xs capitalize">{a.action.replaceAll("_", " ").replaceAll(".", " · ")}</p><p className="mt-1 text-[11px] text-muted-foreground">{stamp(a.at)}</p></div></div>)}{!d.activity.length && <p className="mt-4 text-sm text-muted-foreground">Your recorded actions will appear here.</p>}</section></div><p className="text-xs text-muted-foreground">Application stages are self-reported. These charts show activity, not verified employer responses or hiring predictions.</p></>}</State></div>;
}
export function DashboardTools({
  hidden,
  setHidden
}) {
  const state = usePersonal("/personal/overview"),
    layout = usePersonal("/personal/layout");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (layout.data) setHidden(layout.data.hidden_widgets);
  }, [layout.data, setHidden]);
  const toggle = async key => {
    setBusy(true);
    const next = hidden.includes(key) ? hidden.filter(k => k !== key) : [...hidden, key];
    try {
      const r = await api.put("/personal/layout", {
        version: layout.data.version,
        hidden_widgets: next
      });
      layout.setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
      layout.load();
    } finally {
      setBusy(false);
    }
  };
  return <div className="space-y-4"><div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{[["/tasks", "Open tasks", state.data?.open_tasks, "Plan your next move"], ["/tasks", "Overdue tasks", state.data?.overdue_tasks, "Review your deadlines"], ["/offers", "Active offers", state.data?.active_offers, "Compare your options"], ["/practice", "Practice workbooks", state.data?.practice_sessions, "Prepare a clearer story"]].map(([to, title, n, hint]) => <Link className="panel p-4 hover:border-primary/40" to={to} key={title}><p className="text-xs text-muted-foreground">{title}</p><p className="mt-2 text-2xl font-bold">{n ?? "—"}</p><p className="mt-2 text-[11px] text-primary">{hint} →</p></Link>)}</div>{state.error && <p role="alert" className="text-xs text-destructive">{state.error}</p>}<details className="panel p-4"><summary className="cursor-pointer text-xs font-medium">Customise your overview</summary><p className="mt-3 text-xs text-muted-foreground">Choose the sections you want to see. Changes save to your account.</p>{layout.error && <p role="alert" className="mt-2 text-xs text-destructive">{layout.error}</p>}<div className="mt-4 flex flex-wrap gap-4">{[["opportunities", "Opportunities"], ["pipeline", "Application journey"], ["documents", "Recent documents"], ["search", "Search summary"], ["upcoming", "Coming up"], ["checklist", "Foundation checklist"]].map(([key, label]) => <label className="flex items-center gap-2 text-xs" key={key}><input type="checkbox" disabled={busy || !layout.data} checked={!hidden.includes(key)} onChange={() => toggle(key)} />{label}</label>)}</div></details></div>;
}
